/**
 * The live tables this device can walk back into.
 *
 * A live potje already survives everything the server can throw at it: state is
 * written to SQLite at every round boundary and reloaded on start-up, and a room
 * that is mid-game is never swept. What was missing was the way back — you had to
 * remember a six-letter code, and the chair was recorded in a single slot that
 * the next game overwrote.
 *
 * So this pairs the seats this device holds (`sessions.ts`) with what the server
 * says about those rooms, and hands the home screen a list.
 */

import { useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import {
  forgetSeat,
  routeForSeat,
  savedSeats,
  type SavedSeat,
  type SeatMode,
} from '@/live/sessions';

export interface ResumableLive {
  code: string;
  mode: SeatMode;
  /** Where tapping it goes. */
  to: string;
  /** ISO, so the home screen can sort every kind of potje together. */
  savedAt: string;
  /** Null until the server has answered — offline, that is where it stays. */
  phase: string | null;
  players: string[];
}

interface RoomView {
  code: string;
  mode: SeatMode;
  phase: string;
  players: Array<{ seat: number; name: string; isReady: boolean; isConnected: boolean }>;
}

function fromSeat(seat: SavedSeat): ResumableLive {
  return {
    code: seat.code,
    mode: seat.mode,
    to: routeForSeat(seat),
    savedAt: seat.savedAt,
    phase: null,
    players: [],
  };
}

/**
 * Ask the server about each saved seat.
 *
 * Only a plain 404 forgets a seat — that room is genuinely gone, swept hours
 * after it was abandoned. Everything else keeps it: a failed request is usually a
 * campsite with no signal, and dropping the entry then would take away the only
 * pointer to a game that is still sitting on the server. Same reasoning as the
 * sync queue, and the same mistake to avoid.
 */
export function useLiveResume(): ResumableLive[] {
  const [rooms, setRooms] = useState<ResumableLive[]>(() => savedSeats().map(fromSeat));

  useEffect(() => {
    let live = true;

    void (async () => {
      const seats = savedSeats();
      const resolved = await Promise.all(
        seats.map(async (seat): Promise<ResumableLive | null> => {
          try {
            const room = await api<RoomView>(`/api/rooms/${seat.code}`);
            // A finished game is a memory, not somewhere to go back to.
            if (room.phase === 'finished') {
              forgetSeat(seat.code);
              return null;
            }
            return {
              ...fromSeat(seat),
              // The server knows which game it is; a seat saved before this
              // existed guessed 'live'.
              mode: room.mode ?? seat.mode,
              to: routeForSeat({ ...seat, mode: room.mode ?? seat.mode }),
              phase: room.phase,
              players: room.players.map((player) => player.name),
            };
          } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
              forgetSeat(seat.code);
              return null;
            }
            return fromSeat(seat);
          }
        })
      );

      if (live) setRooms(resolved.filter((room): room is ResumableLive => room !== null));
    })();

    return () => {
      live = false;
    };
  }, []);

  return rooms;
}

/** What to say under the name of a room, given what the server told us. */
export function describeRoom(room: ResumableLive): string {
  const who = room.players.length > 0 ? room.players.join(', ') : 'jouw stoel wacht';
  if (room.phase === null) return `${who} · nog niet nagekeken`;
  if (room.phase === 'lobby') return `${who} · nog niet begonnen`;
  return who;
}
